import { DEFAULT_PASSWORD_MIN_LENGTH } from '@blink/shared';
import { Alert, Button, Card, CardContent, Field } from '@blink/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ErrorAlert, PasswordInput } from '@/components/common';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';

/** First sign-in: the courier replaces the temporary password from the team leader. */
export function ChangePassword() {
  const { t, f } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const mismatch = confirm.length > 0 && confirm !== newPassword;

  const save = useMutation({
    mutationFn: () => api<void>('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      navigate('/', { replace: true });
    },
  });

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center p-4">
      <Card>
        <CardContent className="pt-6">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!mismatch) save.mutate();
            }}
          >
            <div>
              <h1 className="text-lg font-semibold">{t.changePassword.title}</h1>
              <p className="text-sm text-muted-foreground">{t.changePassword.subtitle}</p>
            </div>
            <ErrorAlert error={save.error} />
            <Field label={t.changePassword.current} htmlFor="current">
              <PasswordInput id="current" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} required />
            </Field>
            <Field label={t.changePassword.new} htmlFor="new" hint={f(t.changePassword.rules, { min: DEFAULT_PASSWORD_MIN_LENGTH })}>
              <PasswordInput id="new" autoComplete="new-password" value={newPassword} onChange={(e) => setNew(e.target.value)} required />
            </Field>
            <Field label={t.changePassword.confirm} htmlFor="confirm">
              <PasswordInput id="confirm" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </Field>
            {mismatch ? <Alert variant="warning">{t.changePassword.mismatch}</Alert> : null}
            <Button type="submit" size="lg" className="h-12 w-full text-base" disabled={save.isPending || mismatch}>
              {t.changePassword.submit}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
